import type { Resolution } from './types';

export enum AspectRatio {
    LANDSCAPE = 'landscape',
    SQUARE = 'square',
    VERTICAL = 'vertical',
    FOURFIVE = 'fourfive',
}

export const ALL_ASPECT_RATIOS = Object.values(AspectRatio);

interface AspectRatioDefinition {
    readonly name: string;
    readonly ratio: string;
    readonly referenceResolution: Resolution;
    readonly videoHeights: {
        readonly '480p': number;
        readonly '720p': number;
        readonly '1080p': number;
    };
    getResolution: (resolutionValue: number) => Resolution;
    getResolutionValueFromVideoHeight: (height: number) => number;
}

export type AspectRatioConfigurations = {
    [K in AspectRatio]: AspectRatioDefinition;
};

/**
 * Encoders often require even dimensions (e.g. width and height to be divisible by 2).
 */
function even(n: number): number {
    return n % 2 === 0 ? n : n + 1;
}

export const aspectRatioConfigurations: AspectRatioConfigurations = {
    [AspectRatio.LANDSCAPE]: {
        name: 'Landscape',
        ratio: '16:9',
        referenceResolution: { width: 1920, height: 1080 },
        videoHeights: { '480p': 480, '720p': 720, '1080p': 1080 },
        getResolution: (resolutionValue: number) => {
            const height = resolutionValue;
            const width = even(Math.round((height * 16) / 9));
            return {
                width,
                height,
            };
        },
        getResolutionValueFromVideoHeight: (height: number) => height,
    },
    [AspectRatio.SQUARE]: {
        name: 'Square',
        ratio: '1:1',
        referenceResolution: { width: 1080, height: 1080 },
        videoHeights: { '480p': 480, '720p': 720, '1080p': 1080 },
        getResolution: (resolutionValue: number) => ({
            width: resolutionValue,
            height: resolutionValue,
        }),
        getResolutionValueFromVideoHeight: (height: number) => height,
    },
    [AspectRatio.VERTICAL]: {
        name: 'Vertical',
        ratio: '9:16',
        referenceResolution: { width: 1080, height: 1920 },
        videoHeights: { '480p': 854, '720p': 1280, '1080p': 1920 },
        getResolution: (resolutionValue: number) => {
            const width = resolutionValue;
            const height = even(Math.round((resolutionValue * 16) / 9));
            return {
                width,
                height,
            };
        },
        getResolutionValueFromVideoHeight: (height: number) => {
            const aspectRatioDecimal = 9 / 16;
            return Math.round(height * aspectRatioDecimal);
        },
    },
    [AspectRatio.FOURFIVE]: {
        name: 'Portrait',
        ratio: '4:5',
        referenceResolution: { width: 1080, height: 1350 },
        videoHeights: { '480p': 600, '720p': 900, '1080p': 1350 },
        getResolution: (resolutionValue: number) => {
            const width = resolutionValue;
            const height = even(Math.round(resolutionValue * (5 / 4)));
            return {
                width,
                height,
            };
        },
        getResolutionValueFromVideoHeight: (height: number) => {
            const aspectRatioDecimal = 4 / 5;
            return Math.round(height * aspectRatioDecimal);
        },
    },
};

export const SUPPORTED_VIDEO_HEIGHTS = Object.fromEntries(
    Object.entries(aspectRatioConfigurations).map(([key, config]) => [key, config.videoHeights]),
) as Record<AspectRatio, { '480p': number; '720p': number; '1080p': number }>;

export const BASE_SLIDE_DIMENSIONS = Object.fromEntries(
    Object.entries(aspectRatioConfigurations).map(([key, config]) => [
        key,
        config.referenceResolution,
    ]),
) as Record<AspectRatio, { width: number; height: number }>;

export const MAX_WIDTH_ACROSS_ALL_ASPECT_RATIOS = Math.max(
    ...Object.values(aspectRatioConfigurations).map((config) => config.referenceResolution.width),
);

export function getAspectRatioFromTransitionUrl(transitionUrl: string): AspectRatio | null {
    if (transitionUrl.includes('landscape.json')) {
        return AspectRatio.LANDSCAPE;
    }

    if (transitionUrl.includes('square.json')) {
        return AspectRatio.SQUARE;
    }

    if (transitionUrl.includes('vertical.json')) {
        return AspectRatio.VERTICAL;
    }

    if (transitionUrl.includes('fourfive.json')) {
        return AspectRatio.FOURFIVE;
    }

    return null;
}

export function getAspectRatioDisplayName(aspectRatio: AspectRatio): string {
    return aspectRatioConfigurations[aspectRatio].name;
}

export function getAspectRatioDisplayRatio(aspectRatio: AspectRatio): string {
    return aspectRatioConfigurations[aspectRatio].ratio;
}
